import { useNavigate } from 'react-router-dom';
import { GovChip } from '@gov-design-system-ce/react';
import type { DatoveSchrankyDatovaZpravaDto } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DATABOX_FOLDER_LABELS,
	DataBoxFolderType,
	formatIdentification,
	useDataBoxesContext,
	useDataBoxMessageDetailContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import {
	Button,
	Chip,
	CopyButton,
	DataTable,
	Icon,
	InfoTooltip,
	ToggleButton,
} from '@gov-nx/ui/web';
import { toDateTimeReadable } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { DataBoxAttachmentList } from './DataBoxAttachmentList';
import { DataBoxMessageDataBoxDetail } from './DataBoxMessageDataBoxDetail';
import { DataBoxMessageMySubmission } from './DataBoxMessageMySubmission';

interface DataBoxMessageOverviewProps {
	message: DatoveSchrankyDatovaZpravaDto;
}

export const DataBoxMessageOverview = ({
	message,
}: DataBoxMessageOverviewProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const navigate = useNavigate();

	const {
		dataBoxId,
		folderType,
		recipientDataBox,
		senderDataBox,
		mySubmissionConnection,
		vaultConfirmModal,
		loadingItems,
		dataBox,
	} = useDataBoxMessageDetailContext();

	const { automaticArchiveEnabled } = useDataBoxesContext();

	if (mySubmissionConnection.isLoading) {
		return null;
	}

	return (
		<>
			<Button
				onClick={() =>
					navigate(
						pageRouteResolver('datove-schranky.schranka', {
							dataBoxId,
							folderType,
						})
					)
				}
				color="primary"
				type="base"
				size="m"
				className="[ ds:hidden! ]"
				aria-label={tn(DATABOX_FOLDER_LABELS[folderType])}
				iconStart={
					<Icon
						name="chevron-left"
						type="components"
					/>
				}>
				{tn(DATABOX_FOLDER_LABELS[folderType])}
			</Button>
			<header className="flex flex-col gap-(--spacing-s) h-auto pt-(--templates-margin-m) pb-(--templates-margin-s) px-(--templates-margin-l) mt-0! bg-(--background-block-primary)">
				<div className="flex flex-col [ md:flex-row md:justify-between md:gap-4 ]">
					<h2 className="mb-1 text-body-m-bold! [ md:mb-0 md:text-body-xl-bold! ]">
						{message.vec}
					</h2>
					<time className="text-body-s text-(--text-secondary) whitespace-nowrap [ md:mt-1.5! ]">
						{message.datumACasDodani &&
							toDateTimeReadable(message.datumACasDodani)}
					</time>
				</div>
			</header>

			<div className="bg-(--background-block-primary) [ h-full overflow-y-auto ]">
				<div className="flex flex-col gap-(--spacing-s) h-auto px-(--templates-margin-l) mt-0! bg-(--background-block-primary)">
					<dl className="flex flex-col gap-(--spacing-xs)">
						<div className="flex gap-2">
							<dt className="text-body-s text-(--text-secondary)">
								{tn('zpravy.detail.od')}
							</dt>
							<dd className="w-full text-body-s text-(--text-primary-color)">
								{senderDataBox ? (
									<DataBoxMessageDataBoxDetail
										dataBox={senderDataBox}
										label={`${message.odesilatelNazev} (${message.odesilatelId})`}
									/>
								) : (
									<span className="font-normal text-(--text-primary-color)">{`${message.odesilatelNazev} (${message.odesilatelId})`}</span>
								)}
							</dd>
						</div>
						<div className="flex gap-2">
							<dt className="text-body-s text-(--text-secondary)">
								{tn('zpravy.detail.komu')}
							</dt>
							<dd className="w-full text-body-s text-(--text-primary-color)">
								{recipientDataBox ? (
									<DataBoxMessageDataBoxDetail
										dataBox={recipientDataBox}
										label={`${message.prijemceNazev} (${message.prijemceId})`}
									/>
								) : (
									<span className="font-normal text-(--text-primary-color)">{`${message.prijemceNazev} (${message.prijemceId})`}</span>
								)}
							</dd>
						</div>
						{mySubmissionConnection.isConnected ? (
							<div className="flex gap-2">
								<dt className="text-body-s text-(--text-secondary)">
									{tn('zpravy.detail.pripojeno-k-podani')}
								</dt>
								<dd className="text-body-s text-(--text-primary-color)">
									<DataBoxMessageMySubmission
										submissions={mySubmissionConnection.submissions}
									/>
								</dd>
							</div>
						) : null}
					</dl>

					<div className="flex flex-wrap gap-2 items-center pt-1">
						{dataBox?.typSchranky !== 'PO' &&
							dataBox?.typSchranky !== 'PO_REQ' &&
							[DataBoxFolderType.Received, DataBoxFolderType.Sent].includes(
								folderType
							) &&
							(message.datumArchivace ? (
								<Chip
									color="success"
									size="xs"
									type="outlined"
									iconStart={
										<Icon
											name="archived-outline"
											type="templates"
										/>
									}
									className="w-fit">
									{tn('zpravy.detail.archivovana')}
								</Chip>
							) : (
								automaticArchiveEnabled && (
									<Chip
										color="neutral"
										size="xs"
										type="outlined"
										iconStart={
											<Icon
												name="archive"
												type="templates"
											/>
										}
										className="w-fit">
										{tn('zpravy.detail.bude-archivovana-automaticky')}
									</Chip>
								)
							))}
						{[DataBoxFolderType.Archived, DataBoxFolderType.Trash].includes(
							folderType
						) &&
							(dataBoxId === message.prijemceId ? (
								<Chip
									color="neutral"
									size="xs"
									type="outlined"
									iconStart={
										<Icon
											name="envelope"
											type="templates"
										/>
									}
									className="w-fit">
									{tn('slozky.dorucene')}
								</Chip>
							) : (
								<Chip
									color="neutral"
									size="xs"
									type="outlined"
									iconStart={
										<Icon
											name="send"
											type="templates"
										/>
									}
									className="w-fit">
									{tn('slozky.odeslane')}
								</Chip>
							))}
						{message.vTrezoru && (
							<>
								<GovChip
									color="warning"
									size="xs"
									type="outlined">
									<Icon
										type="templates"
										name="in-trezor"
									/>
									{tn('zpravy.detail.v-datovem-trezoru')}
								</GovChip>
								<Button
									onClick={vaultConfirmModal.setTrue}
									type="base"
									color="error"
									size="xs"
									nativeType="button"
									isLoading={!!loadingItems.deleteFromVault}
									isDisabled={!!loadingItems.deleteFromVault}
									aria-label={tn('zpravy.detail.odstranit-z-datoveho-trezoru')}>
									{tn('zpravy.detail.odstranit-z-datoveho-trezoru')}
								</Button>
							</>
						)}
					</div>
				</div>

				<section className="px-(--templates-margin-l) mb-(--templates-margin-l)!">
					<ToggleButton size="s">
						<div className="ds-more-info-table flex flex-col gap-6">
							<DataTable responsive>
								<tbody>
									<tr>
										<td>{tn('zpravy.detail.id-zpravy')}</td>
										<td>
											<strong>{message.datovaZpravaId}</strong>
											{message.datovaZpravaId && (
												<CopyButton
													label={tn('schranky.zkopirovat-id-schranky')}
													value={message.datovaZpravaId}
												/>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.odesilatel')}</td>
										<td>
											{senderDataBox ? (
												<DataBoxMessageDataBoxDetail
													dataBox={senderDataBox}
													label={`${message.odesilatelNazev} (${message.odesilatelId})`}
													position="right"
												/>
											) : (
												<span className="font-normal text-(--text-primary-color)">{`${message.odesilatelNazev} (${message.odesilatelId})`}</span>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.adresat')}</td>
										<td>
											{recipientDataBox ? (
												<DataBoxMessageDataBoxDetail
													dataBox={recipientDataBox}
													label={`${message.prijemceNazev} (${message.prijemceId})`}
													position="right"
												/>
											) : (
												<span className="font-normal text-(--text-primary-color)">{`${message.odesilatelNazev} (${message.odesilatelId})`}</span>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.identifikace')}</td>
										<td>
											<strong>
												{message.odesilatelJmenoUzivatele
													? formatIdentification(
															message.odesilatelJmenoUzivatele
														)
													: null}
											</strong>
										</td>
									</tr>
								</tbody>
							</DataTable>
							<DataTable responsive>
								<tbody>
									<tr>
										<td>{tn('zpravy.detail.datum-dodani')}</td>
										<td>
											<strong>
												{message.datumACasDodani &&
													toDateTimeReadable(message.datumACasDodani)}
											</strong>
											<InfoTooltip
												placement="top"
												size="m"
												content={tn('zpravy.detail.kdy-zprava-dodana')}
											/>
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.datum-doruceni')}</td>
										<td>
											<strong>
												{message.datumACasDoruceni &&
													toDateTimeReadable(message.datumACasDoruceni)}
											</strong>
											<InfoTooltip
												placement="top"
												size="m"
												content={tn('zpravy.detail.kdy-zprava-prevzata')}
											/>
										</td>
									</tr>
								</tbody>
							</DataTable>
							<DataTable responsive>
								<tbody>
									<tr>
										<td>{tn('zpravy.detail.do-vlastnich-rukou')}</td>
										<td>
											<strong>
												{message.doVlastnichRukou === true
													? tn('zpravy.detail.do-vlastnich-rukou-ano')
													: message.doVlastnichRukou === false
														? tn('zpravy.detail.do-vlastnich-rukou-ne')
														: '-'}
											</strong>
											<InfoTooltip
												placement="top"
												size="m"
												content={tn('zpravy.detail.obdoba-modry-pruh')}
											/>
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.k-rukam')}</td>
										<td>
											<strong>{message.kRukam ?? '-'}</strong>
										</td>
									</tr>
								</tbody>
							</DataTable>
							<DataTable responsive>
								<tbody>
									<tr>
										<td>{tn('zpravy.detail.nase-cislo-jednaci')}</td>
										<td>
											<strong>{message.naseCisloJednaci ?? '-'}</strong>
											{message.naseCisloJednaci && (
												<CopyButton
													label={tn(
														'zpravy.detail.zkopirovat-nase-cislo-jednaci'
													)}
													value={message.naseCisloJednaci}
												/>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.nase-spisova-znacka')}</td>
										<td>
											<strong>{message.naseSpisovaZnacka ?? '-'}</strong>
											{message.naseSpisovaZnacka && (
												<CopyButton
													label={tn(
														'zpravy.detail.zkopirovat-nase-spisova-znacka'
													)}
													value={message.naseSpisovaZnacka}
												/>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.vase-cislo-jednaci')}</td>
										<td>
											<strong>{message.vaseCisloJednaci ?? '-'}</strong>
											{message.vaseCisloJednaci && (
												<CopyButton
													label={tn(
														'zpravy.detail.zkopirovat-vase-cislo-jednaci'
													)}
													value={message.vaseCisloJednaci}
												/>
											)}
										</td>
									</tr>
									<tr>
										<td>{tn('zpravy.detail.vase-spisova-znacka')}</td>
										<td>
											<strong>{message.vaseSpisovaZnacka ?? '-'}</strong>
											{message.vaseSpisovaZnacka && (
												<CopyButton
													label={tn(
														'zpravy.detail.zkopirovat-vase-spisova-znacka'
													)}
													value={message.vaseSpisovaZnacka}
												/>
											)}
										</td>
									</tr>
								</tbody>
							</DataTable>
						</div>
					</ToggleButton>
				</section>

				<DataBoxAttachmentList message={message} />
			</div>
		</>
	);
};
